import { useState } from 'react';
import { useLazyImage } from '../../../hooks/useLazyImage';
import { VideoItem } from '../../../mocks/videos';

interface VideoCardProps {
  video: VideoItem;
}

export default function VideoCard({ video }: VideoCardProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const { ref, isVisible } = useLazyImage({ rootMargin: '200px' });

  const isFeatured = !!video.featured;

  const embedUrl =
    video.platform === 'youtube'
      ? `https://www.youtube.com/embed/${video.videoId}?autoplay=1&rel=0&modestbranding=1`
      : null;

  const isEmbeddable = !!embedUrl;

  const thumbnail = (
    <div
      ref={ref}
      className={`relative bg-gray-900 flex-shrink-0 ${
        isFeatured
          ? 'aspect-video md:aspect-auto md:w-[56%] md:min-h-[280px]'
          : 'aspect-video'
      }`}
    >
      {isPlaying && isEmbeddable ? (
        <iframe
          src={embedUrl!}
          title={video.title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <>
          {isVisible ? (
            <img
              src={video.thumbnail}
              alt={video.title}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              width={800}
              height={450}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gray-800 animate-pulse" aria-hidden="true" />
          )}

          {/* Play Button */}
          {isVisible && isEmbeddable && (
            <button
              onClick={() => setIsPlaying(true)}
              className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
              aria-label={`Play video: ${video.title}`}
            >
              <div
                className={`bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform ${
                  isFeatured ? 'w-24 h-24' : 'w-20 h-20'
                }`}
              >
                <i
                  className={`ri-play-fill text-[#1C2B1E] ml-1 ${isFeatured ? 'text-5xl' : 'text-4xl'}`}
                  aria-hidden="true"
                />
              </div>
            </button>
          )}

          {/* TikTok link-out */}
          {isVisible && !isEmbeddable && (
            <a
              href="https://www.tiktok.com/@rockhoundauto"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
              aria-label={`Watch on TikTok: ${video.title}`}
            >
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-tiktok-fill text-4xl text-[#1C2B1E]" aria-hidden="true" />
              </div>
            </a>
          )}

          {/* Duration */}
          {isVisible && (
            <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/80 text-white rounded-full text-sm font-medium">
              {video.duration}
            </div>
          )}

          {/* Category Badge */}
          {isVisible && (
            <div
              className={`absolute top-4 left-4 px-4 py-2 rounded-full font-bold text-sm uppercase ${
                video.category === 'walkaround' || video.category === 'features'
                  ? 'bg-[#E8B84B] text-[#1C2B1E]'
                  : 'bg-[#1C2B1E] text-white'
              }`}
            >
              {video.category.replace('-', ' ')}
            </div>
          )}

          {/* Featured badge */}
          {isFeatured && isVisible && (
            <div className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1.5 bg-[#E8B84B] text-[#1C2B1E] rounded-full text-xs font-bold">
              <i className="ri-star-fill" aria-hidden="true" />
              Featured
            </div>
          )}
        </>
      )}
    </div>
  );

  const content = (
    <div className="p-6 flex flex-col justify-between flex-1">
      <div>
        <h3
          className={`font-['Oswald'] font-bold text-[#1C2B1E] mb-2 group-hover:text-[#E8B84B] transition-colors ${
            isFeatured ? 'text-2xl md:text-3xl line-clamp-3' : 'text-xl line-clamp-2'
          }`}
        >
          {video.title}
        </h3>
        <p
          className={`text-gray-600 text-sm mb-4 ${
            isFeatured ? 'line-clamp-4' : 'line-clamp-2'
          }`}
        >
          {video.description}
        </p>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-[#E8E2D6]">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <i className="ri-eye-line text-[#E8B84B]" />
            {video.views.toLocaleString()}
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-calendar-line text-[#E8B84B]" />
            {new Date(video.publishedDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`bg-white rounded-2xl border border-[#E8E2D6] overflow-hidden hover:shadow-xl transition-all duration-300 group h-full ${
        isFeatured ? 'flex flex-col md:flex-row' : 'flex flex-col'
      }`}
    >
      {thumbnail}
      {content}
    </div>
  );
}
